<template>
    <div>
        <!-- ========== FULL HEADER (visible when not scrolled) ========== -->
        <MAppBar
            color="white"
            :elevation="0"
            density="comfortable"
        >
            <div class="m-container header-row d-flex align-center">
                <!-- Logo -->
                <NuxtLink
                    to="/"
                    class="d-flex align-center text-decoration-none"
                >
                    <img
                        src="~/assets/img/moebix-logo.svg"
                        alt="moebix"
                        height="48"
                        class="d-none d-sm-block"
                    />
                    <img
                        src="~/assets/img/moebix-logo-mobil.svg"
                        alt="moebix"
                        height="48"
                        class="d-sm-none"
                    />
                </NuxtLink>

                <span class="m-spacer" />

                <!-- Suchbar -->
                <div ref="searchWrapperRef" class="search-bar mx-4">
                    <MTextField
                        v-model="searchQuery"
                        density="comfortable"
                        variant="outlined"
                        color="primary"
                        placeholder="Möbel suchen..."
                        prepend-inner-icon="magnify"
                        :append-inner-icon="
                            searchQuery ? 'close' : undefined
                        "
                        hide-details
                        single-line
                        rounded
                        @keyup.enter="handleSearch"
                        @keydown.escape="searchDropdownOpen = false"
                        @update:model-value="onSearchInput"
                        @focus="
                            if (searchQuery.length >= 2)
                                searchDropdownOpen = true;
                        "
                        @click:append-inner="
                            searchQuery = '';
                            searchDropdownOpen = false;
                            searchResults = null;
                        "
                    />
                </div>

                <!-- Dropdown via Teleport (outside MAppBar overflow) -->
                <ClientOnly>
                    <Teleport to="body">
                        <MCard
                            v-if="
                                searchDropdownOpen &&
                                (searchLoading || searchResults)
                            "
                            ref="searchDropdownRef"
                            :style="dropdownStyle"
                            class="search-dropdown-card"
                            rounded="md"
                            :elevation="8"
                            :max-height="480"
                        >
                            <!-- Loading -->
                            <div
                                v-if="searchLoading"
                                class="pa-4 d-flex align-center ga-3"
                            >
                                <MProgressCircular
                                    :size="20"
                                    :width="2"
                                    indeterminate
                                    color="primary"
                                />
                                <span class="text-body-2 text-medium-emphasis"
                                    >Suche läuft…</span
                                >
                            </div>

                            <template v-else-if="searchResults">
                                <!-- Keine Ergebnisse -->
                                <div
                                    v-if="
                                        !searchResults.categories?.length &&
                                        !searchResults.products?.length
                                    "
                                    class="pa-6 text-center"
                                >
                                    <MIcon
                                        :size="32"
                                        color="grey-lighten-2"
                                        class="mb-2"
                                        icon="magnify-close"
                                    />
                                    <p class="text-body-2 text-medium-emphasis">
                                        Keine Ergebnisse für "{{ searchQuery }}"
                                    </p>
                                </div>

                                <template v-else>
                                    <!-- Kategorien -->
                                    <div
                                        v-if="searchResults.categories?.length"
                                    >
                                        <div
                                            class="px-4 pt-3 pb-1 text-caption text-medium-emphasis font-weight-bold text-uppercase"
                                        >
                                            Kategorien
                                        </div>
                                        <MList
                                            density="compact"
                                            nav
                                            class="py-0"
                                        >
                                            <MListItem
                                                v-for="cat in searchResults.categories.slice(
                                                    0,
                                                    4,
                                                )"
                                                :key="cat.id"
                                                rounded
                                                @click="goToSearchCategory(cat)"
                                            >
                                                <template #prepend>
                                                    <MIcon
                                                        :size="18"
                                                        color="primary"
                                                        icon="folder-outline"
                                                    />
                                                </template>
                                                <template #title>
                                                    <span class="text-body-2">{{
                                                        cat.name
                                                    }}</span>
                                                </template>
                                                <template v-if="cat.parent" #subtitle>
                                                    <span class="text-caption">{{
                                                        cat.parent.name
                                                    }}</span>
                                                </template>
                                            </MListItem>
                                        </MList>
                                        <hr
                                            v-if="
                                                searchResults.products?.length
                                            "
                                            class="m-divider"
                                        />
                                    </div>

                                    <!-- Produkte -->
                                    <div v-if="searchResults.products?.length">
                                        <div
                                            class="px-4 pt-3 pb-1 text-caption text-medium-emphasis font-weight-bold text-uppercase"
                                        >
                                            Produkte
                                        </div>
                                        <MList
                                            density="compact"
                                            nav
                                            class="py-0"
                                        >
                                            <MListItem
                                                v-for="product in searchResults.products.slice(
                                                    0,
                                                    5,
                                                )"
                                                :key="product.id"
                                                :to="getProductUrl(product)"
                                                rounded
                                                @click="
                                                    searchDropdownOpen = false
                                                "
                                            >
                                                <template #prepend>
                                                    <MAvatar
                                                        :size="36"
                                                        rounded
                                                    >
                                                        <MImg
                                                            v-if="product.image"
                                                            :src="product.image"
                                                            cover
                                                        />
                                                        <MIcon
                                                            v-else
                                                            :size="18"
                                                            color="grey"
                                                            icon="sofa-outline"
                                                        />
                                                    </MAvatar>
                                                </template>
                                                <template #title>
                                                    <span class="text-body-2">{{
                                                        product.name
                                                    }}</span>
                                                </template>
                                                <template #subtitle>
                                                    <span
                                                        class="text-primary font-weight-bold"
                                                        >{{
                                                            formatPrice(
                                                                product.price,
                                                            )
                                                        }}</span
                                                    >
                                                    <span
                                                        v-if="product.brand"
                                                        class="ml-2 text-medium-emphasis"
                                                        >{{
                                                            product.brand
                                                        }}</span
                                                    >
                                                </template>
                                            </MListItem>
                                        </MList>
                                    </div>

                                    <!-- Alle anzeigen -->
                                    <hr class="m-divider" />
                                    <div class="pa-2">
                                        <MBtn
                                            variant="text"
                                            color="primary"
                                            size="small"
                                            class="text-none w-100"
                                            @click="handleSearch"
                                        >
                                            Alle Ergebnisse für "{{
                                                searchQuery
                                            }}" anzeigen
                                            <MIcon end :size="16" icon="arrow-right" />
                                        </MBtn>
                                    </div>
                                </template>
                            </template>
                        </MCard>
                    </Teleport>
                </ClientOnly>

                <span class="m-spacer" />

                <!-- Wishlist (nur Desktop) -->
                <MBtn
                    icon
                    variant="text"
                    to="/merkliste"
                    size="small"
                    class="mr-1 d-none d-lg-flex"
                >
                    <MBadge
                        :content="wishlistStore.totalItems"
                        :model-value="wishlistStore.totalItems > 0"
                        color="secondary"
                        size="x-small"
                    >
                        <MIcon :size="22" icon="heart-outline" />
                    </MBadge>
                </MBtn>

                <!-- Login/Register Dropdown (nur Desktop) -->
                <MMenu>
                    <template #activator="{ props: menuProps }">
                        <MBtn v-bind="menuProps" icon variant="text" size="small" class="d-none d-lg-flex" aria-label="Konto-Menü">
                            <MAvatar
                                v-if="authStore.isAuthenticated"
                                color="primary"
                                :size="30"
                            >
                                <MIcon icon="account" size="small" />
                            </MAvatar>
                            <MIcon v-else :size="22" icon="account-circle-outline" />
                        </MBtn>
                    </template>
                    <MList
                        density="compact"
                        :min-width="200"
                        :elevation="3"
                        rounded
                    >
                        <template v-if="authStore.isAuthenticated">
                            <MListItem
                                class="text-caption text-medium-emphasis"
                            >
                                {{
                                    authStore.user?.name ||
                                    authStore.user?.email
                                }}
                            </MListItem>
                            <hr class="m-divider" />
                            <MListItem
                                to="/dashboard"
                                prepend-icon="view-dashboard"
                                title="Dashboard"
                            />
                            <MListItem
                                v-if="authStore.isAdmin || authStore.isDev"
                                to="/admin"
                                prepend-icon="shield-crown"
                                title="Admin"
                            />
                            <MListItem
                                to="/merkliste"
                                prepend-icon="heart-outline"
                                title="Merkliste"
                            />
                            <hr class="m-divider" />
                            <MListItem
                                @click="handleLogout"
                                prepend-icon="logout"
                                title="Abmelden"
                            />
                        </template>
                        <template v-else>
                            <MListItem
                                to="/auth/login"
                                prepend-icon="login"
                                title="Anmelden"
                            />
                            <MListItem
                                to="/auth/register"
                                prepend-icon="account-plus"
                                title="Registrieren"
                            />
                        </template>
                    </MList>
                </MMenu>

                <!-- Mobile Hamburger -->
                <MBtn icon variant="text"
                    class="d-lg-none"
                    :aria-label="mobileDrawer ? 'Menü schließen' : 'Menü öffnen'"
                    :aria-expanded="mobileDrawer"
                    @click="mobileDrawer = !mobileDrawer"
                >
                    <MIcon :icon="mobileDrawer ? 'close' : 'menu'" />
                </MBtn>
            </div>

            <!-- Ebene 2: Kategorien im Extension-Slot (nur Desktop) -->
            <template #extension>
                <div class="nav-extension d-none d-lg-flex">
                    <div class="m-container header-row d-flex align-center pa-0">
                        <!-- Skeleton while loading -->
                        <template v-if="navigationStore.loading">
                            <MSkeletonLoader
                                v-for="n in 8"
                                :key="n"
                                type="text"
                                :width="90"
                                class="mx-2"
                            />
                        </template>
                        <template
                            v-for="cat in navigationStore.items"
                            :key="cat.id"
                        >
                            <MMenu
                                v-model="openMenus[cat.id]"
                                open-on-hover
                                :close-on-content-click="true"
                                full-width
                                :open-delay="100"
                                :close-delay="150"
                            >
                                <template #activator="{ props: menuProps, isActive }">
                                    <NuxtLink
                                        v-bind="menuProps"
                                        :to="getCatPath(cat)"
                                        class="nav-cat-link"
                                        :class="{
                                            'nav-cat-link--active':
                                                isActive ||
                                                isActivePath(getCatPath(cat)),
                                        }"
                                        @click="openMenus[cat.id] = false"
                                    >
                                        {{ cat.name }}
                                        <MIcon
                                            v-if="cat.children?.length"
                                            :size="14"
                                            class="nav-cat-link__icon"
                                            :class="{
                                                'nav-cat-link__icon--open':
                                                    isActive,
                                            }"
                                            icon="chevron-down"
                                        />
                                    </NuxtLink>
                                </template>
                                <MSheet
                                    v-if="cat.children?.length"
                                    class="mega-menu"
                                    :elevation="8"
                                    rounded="b"
                                >
                                    <!-- Header -->
                                    <div class="mega-menu__header">
                                        <div>
                                            <span class="mega-menu__title">
                                                <MIcon :icon="categoryIcon(cat.slug)" :size="22" />
                                                {{ cat.name }}
                                            </span>
                                            <p v-if="cat.titleDescription" class="mega-menu__subtitle">{{ cat.titleDescription }}</p>
                                        </div>
                                        <NuxtLink
                                            :to="getCatPath(cat)"
                                            class="mega-menu__all-link"
                                        >
                                            Alle {{ cat.name }} ansehen
                                            <MIcon size="small" icon="arrow-right" />
                                        </NuxtLink>
                                    </div>
                                    <hr class="m-divider" />
                                    <!-- Subcategories Grid (3-Level) -->
                                    <div class="mega-menu__body">
                                        <div class="m-row" style="margin: 0">
                                            <div
                                                v-for="(
                                                    chunk, ci
                                                ) in splitColumns(
                                                    cat.children,
                                                    4,
                                                )"
                                                :key="ci"
                                                class="m-col m-col-3"
                                            >
                                                <template
                                                    v-for="sub in chunk"
                                                    :key="sub.id"
                                                >
                                                    <template
                                                        v-if="
                                                            sub.children?.length
                                                        "
                                                    >
                                                        <NuxtLink
                                                            :to="
                                                                getCatPath(sub)
                                                            "
                                                            class="mega-menu__group-title"
                                                            :class="{
                                                                'mega-menu__group-title--active':
                                                                    isActivePath(
                                                                        getCatPath(
                                                                            sub,
                                                                        ),
                                                                    ),
                                                            }"
                                                        >
                                                            <MIcon :icon="categoryIcon(sub.slug)" :size="18" class="mega-menu__icon" />
                                                            {{ sub.name }}
                                                        </NuxtLink>
                                                        <NuxtLink
                                                            v-for="child in sub.children"
                                                            :key="child.id"
                                                            :to="
                                                                getCatPath(
                                                                    child,
                                                                )
                                                            "
                                                            class="mega-menu__link mega-menu__link--child"
                                                            :class="{
                                                                'mega-menu__link--active':
                                                                    isActivePath(
                                                                        getCatPath(
                                                                            child,
                                                                        ),
                                                                    ),
                                                            }"
                                                        >
                                                            <MIcon :icon="categoryIcon(child.slug)" :size="16" class="mega-menu__icon" />
                                                            {{ child.name }}
                                                        </NuxtLink>
                                                    </template>
                                                    <NuxtLink
                                                        v-else
                                                        :to="getCatPath(sub)"
                                                        class="mega-menu__link"
                                                        :class="{
                                                            'mega-menu__link--active':
                                                                isActivePath(
                                                                    getCatPath(
                                                                        sub,
                                                                    ),
                                                                ),
                                                        }"
                                                    >
                                                        <MIcon :icon="categoryIcon(sub.slug)" :size="14" class="mega-menu__icon" />
                                                        {{ sub.name }}
                                                    </NuxtLink>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </MSheet>
                            </MMenu>
                        </template>

                        <!-- Magazin als letztes Item -->
                        <NuxtLink
                            to="/magazin"
                            class="nav-cat-link nav-cat-link--magazin"
                        >
                            <MIcon :size="16" class="mr-1" icon="newspaper-variant-outline" />
                            Magazin
                        </NuxtLink>
                    </div>
                </div>
            </template>
        </MAppBar>

        <!-- ========== COMPACT HEADER (visible when scrolled) ========== -->
        <ClientOnly>
            <div class="compact-header" :class="{ 'compact-header--visible': isScrolled }">
                <div class="m-container header-row d-flex align-center py-0">
                    <!-- Logo (compact) -->
                    <NuxtLink
                        to="/"
                        class="d-flex align-center text-decoration-none"
                    >
                        <img
                            src="~/assets/img/moebix-logo-mobil.svg"
                            alt="Moebix"
                            class="compact-logo"
                        />
                    </NuxtLink>

                    <span class="m-spacer" />

                    <!-- Mobile: Suchbar (gleich wie Full Header) -->
                    <div class="search-bar mx-4 d-none d-sm-block d-lg-none">
                        <MTextField
                            v-model="searchQuery"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            placeholder="Möbel suchen..."
                            prepend-inner-icon="magnify"
                            hide-details
                            single-line
                            rounded
                            @keyup.enter="handleSearch"
                        />
                    </div>

                    <!-- Desktop: Kategorie Links inline -->
                    <div class="compact-nav d-none d-lg-flex align-center">
                        <template
                            v-for="cat in navigationStore.items"
                            :key="'compact-' + cat.id"
                        >
                            <MMenu
                                v-model="openMenus['c-' + cat.id]"
                                open-on-hover
                                :close-on-content-click="true"
                                full-width
                                :open-delay="100"
                                :close-delay="150"
                            >
                                <template #activator="{ props: menuProps, isActive }">
                                    <NuxtLink
                                        v-bind="menuProps"
                                        :to="getCatPath(cat)"
                                        class="compact-nav__link"
                                        :class="{
                                            'compact-nav__link--active':
                                                isActive ||
                                                isActivePath(getCatPath(cat)),
                                        }"
                                        @click="
                                            openMenus['c-' + cat.id] = false
                                        "
                                    >
                                        {{ cat.name }}
                                        <MIcon
                                            v-if="cat.children?.length"
                                            :size="12"
                                            class="compact-nav__icon"
                                            :class="{
                                                'compact-nav__icon--open':
                                                    isActive,
                                            }"
                                            icon="chevron-down"
                                        />
                                    </NuxtLink>
                                </template>
                                <MSheet
                                    v-if="cat.children?.length"
                                    class="mega-menu"
                                    :elevation="8"
                                    rounded="b"
                                >
                                    <div class="mega-menu__header">
                                        <div>
                                            <span class="text-h6 font-weight-bold">{{ cat.name }}</span>
                                            <p v-if="cat.titleDescription" class="mega-menu__subtitle">{{ cat.titleDescription }}</p>
                                        </div>
                                        <NuxtLink
                                            :to="getCatPath(cat)"
                                            class="mega-menu__all-link"
                                        >
                                            Alle {{ cat.name }} ansehen
                                            <MIcon size="small" icon="arrow-right" />
                                        </NuxtLink>
                                    </div>
                                    <hr class="m-divider" />
                                    <div class="mega-menu__body">
                                        <div class="m-row" style="margin: 0">
                                            <div
                                                v-for="(
                                                    chunk, ci
                                                ) in splitColumns(
                                                    cat.children,
                                                    4,
                                                )"
                                                :key="ci"
                                                class="m-col m-col-3"
                                            >
                                                <template
                                                    v-for="sub in chunk"
                                                    :key="sub.id"
                                                >
                                                    <template
                                                        v-if="
                                                            sub.children?.length
                                                        "
                                                    >
                                                        <NuxtLink
                                                            :to="
                                                                getCatPath(sub)
                                                            "
                                                            class="mega-menu__group-title"
                                                            :class="{
                                                                'mega-menu__group-title--active':
                                                                    isActivePath(
                                                                        getCatPath(
                                                                            sub,
                                                                        ),
                                                                    ),
                                                            }"
                                                        >
                                                            <MIcon :icon="categoryIcon(sub.slug)" :size="18" class="mega-menu__icon" />
                                                            {{ sub.name }}
                                                        </NuxtLink>
                                                        <NuxtLink
                                                            v-for="child in sub.children"
                                                            :key="child.id"
                                                            :to="
                                                                getCatPath(
                                                                    child,
                                                                )
                                                            "
                                                            class="mega-menu__link mega-menu__link--child"
                                                            :class="{
                                                                'mega-menu__link--active':
                                                                    isActivePath(
                                                                        getCatPath(
                                                                            child,
                                                                        ),
                                                                    ),
                                                            }"
                                                        >
                                                            <MIcon :icon="categoryIcon(child.slug)" :size="16" class="mega-menu__icon" />
                                                            {{ child.name }}
                                                        </NuxtLink>
                                                    </template>
                                                    <NuxtLink
                                                        v-else
                                                        :to="getCatPath(sub)"
                                                        class="mega-menu__link"
                                                        :class="{
                                                            'mega-menu__link--active':
                                                                isActivePath(
                                                                    getCatPath(
                                                                        sub,
                                                                    ),
                                                                ),
                                                        }"
                                                    >
                                                        <MIcon :icon="categoryIcon(sub.slug)" :size="14" class="mega-menu__icon" />
                                                        {{ sub.name }}
                                                    </NuxtLink>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </MSheet>
                            </MMenu>
                        </template>

                        <!-- Magazin -->
                        <NuxtLink
                            to="/magazin"
                            class="compact-nav__link compact-nav__link--magazin"
                        >
                            <MIcon :size="14" class="mr-1" icon="newspaper-variant-outline" />
                            Magazin
                        </NuxtLink>
                    </div>

                    <span class="m-spacer" />

                    <!-- Search Icon (opens overlay) -->
                    <MBtn
                        icon
                        variant="text"
                        size="small"
                        :aria-label="compactSearchOpen ? 'Suche schließen' : 'Suche öffnen'"
                        :aria-expanded="compactSearchOpen"
                        @click="compactSearchOpen = !compactSearchOpen"
                    >
                        <MIcon :size="20" icon="magnify" />
                    </MBtn>

                    <!-- Wishlist (nur Desktop) -->
                    <MBtn icon variant="text" to="/merkliste" size="small" class="d-none d-lg-flex" aria-label="Merkliste">
                        <MBadge
                            :content="wishlistStore.totalItems"
                            :model-value="wishlistStore.totalItems > 0"
                            color="secondary"
                            size="x-small"
                        >
                            <MIcon :size="20" icon="heart-outline" />
                        </MBadge>
                    </MBtn>

                    <!-- Login (nur Desktop) -->
                    <MMenu>
                        <template #activator="{ props: menuProps }">
                            <MBtn
                                v-bind="menuProps"
                                icon
                                variant="text"
                                size="small"
                                class="d-none d-lg-flex"
                            >
                                <MAvatar
                                    v-if="authStore.isAuthenticated"
                                    color="primary"
                                    :size="26"
                                >
                                    <MIcon icon="account" size="x-small" />
                                </MAvatar>
                                <MIcon v-else :size="20" icon="account-circle-outline" />
                            </MBtn>
                        </template>
                        <MList
                            density="compact"
                            :min-width="200"
                            :elevation="3"
                            rounded
                        >
                            <template v-if="authStore.isAuthenticated">
                                <MListItem
                                    class="text-caption text-medium-emphasis"
                                >
                                    {{
                                        authStore.user?.name ||
                                        authStore.user?.email
                                    }}
                                </MListItem>
                                <hr class="m-divider" />
                                <MListItem
                                    to="/dashboard"
                                    prepend-icon="view-dashboard"
                                    title="Dashboard"
                                />
                                <MListItem
                                    v-if="authStore.isAdmin || authStore.isDev"
                                    to="/admin"
                                    prepend-icon="shield-crown"
                                    title="Admin"
                                />
                                <MListItem
                                    to="/merkliste"
                                    prepend-icon="heart-outline"
                                    title="Merkliste"
                                />
                                <hr class="m-divider" />
                                <MListItem
                                    @click="handleLogout"
                                    prepend-icon="logout"
                                    title="Abmelden"
                                />
                            </template>
                            <template v-else>
                                <MListItem
                                    to="/auth/login"
                                    prepend-icon="login"
                                    title="Anmelden"
                                />
                                <MListItem
                                    to="/auth/register"
                                    prepend-icon="account-plus"
                                    title="Registrieren"
                                />
                            </template>
                        </MList>
                    </MMenu>

                    <!-- Mobile Hamburger -->
                    <MBtn icon variant="text"
                        class="d-lg-none"
                        size="small"
                        :aria-label="mobileDrawer ? 'Menü schließen' : 'Menü öffnen'"
                        :aria-expanded="mobileDrawer"
                        @click="mobileDrawer = !mobileDrawer"
                    >
                        <MIcon :icon="mobileDrawer ? 'close' : 'menu'" />
                    </MBtn>
                </div>
            </div>
            <!-- Compact Search Overlay -->
            <Transition name="expand">
                <div
                    v-if="isScrolled && compactSearchOpen"
                    class="compact-search-bar"
                >
                    <div class="m-container py-2">
                        <MTextField
                            ref="compactSearchField"
                            v-model="searchQuery"
                            density="compact"
                            variant="solo-filled"
                            flat
                            placeholder="Möbel suchen..."
                            prepend-inner-icon="magnify"
                            append-inner-icon="close"
                            hide-details
                            single-line
                            bg-color="grey-lighten-4"
                            rounded
                            autofocus
                            @keyup.enter="handleSearch"
                            @keydown.escape="compactSearchOpen = false"
                            @click:append-inner="compactSearchOpen = false"
                        />
                    </div>
                </div>
            </Transition>
        </ClientOnly>

        <!-- ========== MOBILE DRAWER (hierarchical categories) ========== -->
        <ClientOnly>
            <MNavigationDrawer
                v-model="mobileDrawer"
                temporary
                location="right"
                :width="320"
                class="mobile-drawer"
            >
                <!-- Drawer Header -->
                <div class="mobile-drawer__header">
                    <NuxtLink
                        to="/"
                        class="d-flex align-center text-decoration-none"
                        @click="mobileDrawer = false"
                    >
                        <img
                            src="~/assets/img/moebix-logo.svg"
                            alt="Moebix"
                            height="36"
                        />
                    </NuxtLink>
                    <MBtn
                        icon
                        variant="text"
                        size="small"
                        @click="mobileDrawer = false"
                    >
                        <MIcon icon="close" />
                    </MBtn>
                </div>

                <!-- Account-Bereich oben -->
                <div class="mobile-drawer__account">
                    <template v-if="authStore.isAuthenticated">
                        <div class="mobile-drawer__user">
                            <MAvatar color="primary" :size="36">
                                <MIcon icon="account" size="small" color="white" />
                            </MAvatar>
                            <div>
                                <div class="mobile-drawer__user-name">
                                    {{ authStore.user?.name || authStore.user?.email }}
                                </div>
                                <NuxtLink
                                    to="/dashboard"
                                    class="mobile-drawer__user-link"
                                    @click="mobileDrawer = false"
                                >
                                    Mein Konto
                                </NuxtLink>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="mobile-drawer__auth-buttons">
                            <MBtn
                                color="primary"
                                variant="flat"
                                size="small"
                                block
                                to="/auth/login"
                                @click="mobileDrawer = false"
                            >
                                <MIcon icon="login" size="small" class="mr-1" />
                                Anmelden
                            </MBtn>
                            <MBtn
                                variant="outlined"
                                size="small"
                                block
                                to="/auth/register"
                                @click="mobileDrawer = false"
                            >
                                Registrieren
                            </MBtn>
                        </div>
                    </template>
                </div>

                <!-- Mobile Search -->
                <div class="px-4 pb-3">
                    <MTextField
                        v-model="searchQuery"
                        density="compact"
                        variant="solo-filled"
                        flat
                        placeholder="Suchen..."
                        prepend-inner-icon="magnify"
                        hide-details
                        single-line
                        bg-color="grey-lighten-4"
                        rounded
                        @keyup.enter="
                            handleSearch();
                            mobileDrawer = false;
                        "
                    />
                </div>

                <hr class="m-divider" />

                <!-- Hierarchical Navigation -->
                <div class="mobile-drawer__nav">
                    <!-- Back button when inside a subcategory -->
                    <Transition name="slide-back" mode="out-in">
                        <div
                            v-if="mobileNavStack.length > 0"
                            key="back"
                            class="mobile-drawer__back"
                            @click="mobileNavGoBack"
                        >
                            <MIcon :size="20" class="mr-2" icon="arrow-left" />
                            <span class="font-weight-medium">{{
                                currentMobileNavTitle
                            }}</span>
                        </div>
                    </Transition>

                    <!-- Category list -->
                    <Transition :name="mobileNavDirection" mode="out-in">
                        <MList
                            :key="mobileNavKey"
                            density="compact"
                            nav
                            class="py-1"
                        >
                            <!-- Home only at root level -->
                            <MListItem
                                v-if="mobileNavStack.length === 0"
                                to="/"
                                prepend-icon="home-outline"
                                title="Startseite"
                                @click="mobileDrawer = false"
                            />

                            <!-- Category items -->
                            <MListItem
                                v-for="item in currentMobileNavItems"
                                :key="item.id"
                                class="mobile-drawer__item"
                                :class="{
                                    'mobile-drawer__item--active':
                                        isMobileItemActive(item),
                                }"
                                :prepend-icon="mobileNavStack.length === 0 ? categoryIcon(item.slug) : undefined"
                                @click="handleMobileNavClick(item)"
                            >
                                <template #title>
                                    <span
                                        :class="{
                                            'text-secondary font-weight-bold':
                                                isMobileItemActive(item),
                                        }"
                                    >{{ item.name }}</span>
                                </template>
                                <template v-if="item.children?.length" #append>
                                    <MIcon
                                        :size="18"
                                        :color="
                                            isMobileItemActive(item)
                                                ? 'secondary'
                                                : 'grey'
                                        "
                                        icon="chevron-right"
                                    />
                                </template>
                            </MListItem>

                            <!-- Static items at root level -->
                            <template v-if="mobileNavStack.length === 0">
                                <hr class="m-divider my-1" />
                                <MListItem
                                    to="/angebote"
                                    prepend-icon="tag-outline"
                                    title="Angebote"
                                    @click="mobileDrawer = false"
                                />
                                <MListItem
                                    to="/magazin"
                                    prepend-icon="newspaper-variant-outline"
                                    title="Magazin"
                                    @click="mobileDrawer = false"
                                />
                                <MListItem
                                    to="/merkliste"
                                    prepend-icon="heart-outline"
                                    @click="mobileDrawer = false"
                                >
                                    <template #title>
                                        Merkliste
                                        <MBadge
                                            v-if="wishlistStore.totalItems > 0"
                                            :content="wishlistStore.totalItems"
                                            color="secondary"
                                            inline
                                        />
                                    </template>
                                </MListItem>

                                <template v-if="authStore.isAuthenticated">
                                    <hr class="m-divider my-1" />
                                    <MListItem
                                        to="/dashboard"
                                        prepend-icon="view-dashboard-outline"
                                        title="Dashboard"
                                        @click="mobileDrawer = false"
                                    />
                                    <MListItem
                                        v-if="authStore.isAdmin || authStore.isDev"
                                        to="/admin"
                                        prepend-icon="shield-crown"
                                        title="Admin"
                                        @click="mobileDrawer = false"
                                    />
                                    <MListItem
                                        @click="handleLogout"
                                        prepend-icon="logout"
                                        title="Abmelden"
                                    />
                                </template>
                            </template>
                        </MList>
                    </Transition>
                </div>
            </MNavigationDrawer>
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
import { useMediaQuery } from "@vueuse/core";
import { useNavigationStore, type NavItem } from "~/stores/navigation";
import { useAuthStore } from "~/stores/auth";
import { useWishlistStore } from "~/stores/wishlist";
import { getProductUrl } from "~/composables/useProductUrl";

const lgAndUp = useMediaQuery("(min-width: 1280px)");

const navigationStore = useNavigationStore();
const authStore = useAuthStore();
const wishlistStore = useWishlistStore();
const api = useApi();
const mobileDrawer = ref(false);
const openMenus = reactive<Record<string, boolean>>({});
const searchQuery = ref("");
const compactSearchOpen = ref(false);

// Live search
const searchWrapperRef = ref<HTMLElement | null>(null);
const searchDropdownRef = ref<HTMLElement | null>(null);
const searchDropdownOpen = ref(false);
const searchLoading = ref(false);
const searchResults = ref<any>(null);
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const dropdownStyle = ref<Record<string, string>>({});

const updateDropdownPosition = () => {
    if (!searchWrapperRef.value) return;
    const rect = searchWrapperRef.value.getBoundingClientRect();
    dropdownStyle.value = {
        position: "fixed",
        top: `${rect.bottom + 6}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: "2000",
    };
};

const onClickOutside = (e: MouseEvent) => {
    const target = e.target as Node;
    const inInput = searchWrapperRef.value?.contains(target);
    const inDropdown = searchDropdownRef.value?.contains(target);
    if (!inInput && !inDropdown) {
        searchDropdownOpen.value = false;
    }
};

watch(searchDropdownOpen, async (open) => {
    if (open) {
        await nextTick();
        updateDropdownPosition();
    }
});

onMounted(() => {
    document.addEventListener("mousedown", onClickOutside);
    window.addEventListener("resize", updateDropdownPosition);
});
onUnmounted(() => {
    document.removeEventListener("mousedown", onClickOutside);
    window.removeEventListener("resize", updateDropdownPosition);
});

const formatPrice = (price: number) =>
    new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
    }).format(price);

const onSearchInput = async () => {
    if (searchTimeout) clearTimeout(searchTimeout);
    if (searchQuery.value.length < 2) {
        searchDropdownOpen.value = false;
        searchResults.value = null;
        return;
    }
    searchLoading.value = true;
    if (!searchDropdownOpen.value) {
        searchDropdownOpen.value = true;
        await nextTick();
        updateDropdownPosition();
    }
    searchTimeout = setTimeout(async () => {
        try {
            const res = await api<any>("/api/search", {
                query: { q: searchQuery.value },
            });
            searchResults.value = res;
        } catch {
            searchResults.value = null;
        } finally {
            searchLoading.value = false;
        }
    }, 300);
};

// Close dropdown on route change (route declared below)

// --- Scroll detection ---
const isScrolled = ref(false);
const SCROLL_THRESHOLD = 80;

const onScroll = () => {
    isScrolled.value = window.scrollY > SCROLL_THRESHOLD;
    if (!isScrolled.value) {
        compactSearchOpen.value = false;
    }
};

onMounted(async () => {
    await authStore.checkAuth();
    await navigationStore.fetchNavigation();
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
});

onUnmounted(() => {
    window.removeEventListener("scroll", onScroll);
});

// --- Mobile hierarchical navigation ---
interface MobileNavLevel {
    title: string;
    path: string;
    items: NavItem[];
}

const mobileNavStack = ref<MobileNavLevel[]>([]);
const mobileNavDirection = ref<"slide-forward" | "slide-back">("slide-forward");

const currentMobileNavItems = computed(() => {
    const last = mobileNavStack.value[mobileNavStack.value.length - 1];
    return last ? last.items : navigationStore.items;
});

const currentMobileNavTitle = computed(() => {
    const last = mobileNavStack.value[mobileNavStack.value.length - 1];
    return last ? last.title : "";
});

const currentMobileNavPath = computed(() => {
    const last = mobileNavStack.value[mobileNavStack.value.length - 1];
    return last ? last.path : "/";
});

const mobileNavKey = computed(() => {
    if (mobileNavStack.value.length === 0) return "root";
    return mobileNavStack.value.map((l) => l.title).join("/");
});

const route = useRoute();

watch(
    () => route.path,
    () => {
        searchDropdownOpen.value = false;
        searchQuery.value = "";
        searchResults.value = null;
    },
);

const isActivePath = (itemPath: string) => {
    const current = route.path;
    if (!current || !itemPath) return false;
    return current === itemPath || current.startsWith(itemPath + "/");
};

const isMobileItemActive = (item: NavItem) => {
    const itemPath = item.path || `/${item.slug}`;
    return isActivePath(itemPath);
};

const handleMobileNavClick = (item: NavItem) => {
    if (item.children?.length) {
        mobileNavDirection.value = "slide-forward";
        mobileNavStack.value.push({
            title: item.name,
            path: getCatPath(item),
            items: item.children,
        });
    } else {
        navigateTo(getCatPath(item));
        mobileDrawer.value = false;
    }
};

const mobileNavGoBack = () => {
    mobileNavDirection.value = "slide-back";
    mobileNavStack.value.pop();
};

// Auto-expand to current category when drawer opens
watch(mobileDrawer, (open) => {
    if (!open) {
        mobileNavStack.value = [];
    } else {
        // Find and expand active category
        const currentPath = route.path;
        if (currentPath && currentPath !== "/") {
            for (const cat of navigationStore.items) {
                const catPath = getCatPath(cat);
                if (currentPath.startsWith(catPath) && cat.children?.length) {
                    mobileNavStack.value = [
                        {
                            title: cat.name,
                            path: catPath,
                            items: cat.children,
                        },
                    ];
                    // Check if a subcategory matches too
                    for (const sub of cat.children) {
                        const subPath = getCatPath(sub);
                        if (
                            currentPath.startsWith(subPath) &&
                            sub.children?.length
                        ) {
                            mobileNavStack.value.push({
                                title: sub.name,
                                path: subPath,
                                items: sub.children,
                            });
                            break;
                        }
                    }
                    break;
                }
            }
        }
    }
});

const getCatPath = (cat: NavItem) => cat.path;

const getSearchCatPath = (cat: any) => {
    if (cat.parent?.slug) {
        if (cat.parent.parent?.slug) {
            return `/${cat.parent.parent.slug}/${cat.parent.slug}/${cat.slug}`;
        }
        return `/${cat.parent.slug}/${cat.slug}`;
    }
    return `/${cat.slug}`;
};

const goToSearchCategory = (cat: any) => {
    const path = getSearchCatPath(cat);
    searchDropdownOpen.value = false;
    searchQuery.value = "";
    searchResults.value = null;
    navigateTo(path);
};

const splitColumns = (items: any[], cols: number) => {
    if (!items || items.length === 0) return [];
    const perCol = Math.ceil(items.length / cols);
    const result: any[][] = [];
    for (let i = 0; i < cols; i++) {
        const chunk = items.slice(i * perCol, (i + 1) * perCol);
        if (chunk.length > 0) result.push(chunk);
    }
    return result;
};

const handleSearch = () => {
    if (searchQuery.value.trim()) {
        searchDropdownOpen.value = false;
        navigateTo(`/suche?q=${encodeURIComponent(searchQuery.value.trim())}`);
    }
};

const handleLogout = async () => {
    await authStore.logout();
    mobileDrawer.value = false;
};
</script>

<style scoped>
/* Einheitliches Padding für beide Header auf Mobile */
:deep(.m-app-bar__content) {
    padding: 0;
}
.m-container.header-row {
    padding-left: 10px;
    padding-right: 10px;
}
@media (min-width: 600px) {
    .m-container.header-row {
        padding-left: 16px;
        padding-right: 16px;
    }
}

.search-bar {
    flex: 1 1 auto;
    max-width: 100%;
    min-width: 120px;
}

@media (min-width: 600px) {
    .search-bar {
        max-width: 560px;
    }
}

@media (min-width: 1280px) {
    .search-bar {
        max-width: 640px;
    }
}

.search-dropdown-card {
    overflow-y: auto;
}

</style>

<style>
/* ========== EXTENSION BAR ========== */
.m-app-bar__extension {
    background-color: #fff;
    border-top: 1px solid #eee;
    padding: 0;
    height: 40px;
    display: flex;
    align-items: center;
}

/* Extension enthält nur die Desktop-Kategorie-Nav (d-none d-lg-flex).
   Auf < lg Breakpoint ist der Inhalt leer → Leiste selbst ausblenden,
   sonst entsteht unter dem Header ein weißer 40px-Streifen. */
@media (max-width: 1279.98px) {
    .m-app-bar__extension {
        display: none;
    }
}

.nav-extension {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
}

/* ========== KATEGORIE LINKS ========== */
.nav-cat-link {
    color: var(--m-primary);
    font-family: "Play", sans-serif;
    font-size: 14px;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.25px;
    padding: 0 4px;
    height: 100%;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    white-space: nowrap;
}

.nav-cat-link:hover,
.nav-cat-link:focus-visible,
.nav-cat-link--active {
    color: var(--m-secondary);
}

.nav-cat-link__icon {
    transition: transform 0.2s ease;
    opacity: 0.6;
}

.nav-cat-link__icon--open {
    transform: rotate(180deg);
    opacity: 1;
}

.nav-cat-link--magazin {
    font-family: "Justtellmewhat", sans-serif;
    margin-left: auto;
    color: #e34444;
}

.nav-cat-link--magazin:hover {
    color: #c52f2f;
    background-color: rgba(227, 68, 68, 0.08);
}

/* ========== MEGAMENU DROPDOWN ========== */
.mega-menu {
    background: #fff;
    width: 100%;
    max-width: 1280px;
    overflow: hidden;
    border-radius: 0 0 var(--m-radius) var(--m-radius);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.04);
}

.mega-menu__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
}

.mega-menu__body {
    padding: 16px 24px 24px;
}

.mega-menu__all-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #3c8f93;
    font-weight: 600;
    text-decoration: none;
    transition: color 0.15s ease;
}

.mega-menu__all-link:hover {
    color: #e34444;
}

.mega-menu__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--m-on-surface);
}

.mega-menu__subtitle {
    font-size: 0.8125rem;
    color: rgba(0, 0, 0, 0.5);
    margin: 4px 0 0;
    font-weight: 400;
    font-style: normal;
}

.mega-menu__icon {
    opacity: 0.5;
    flex-shrink: 0;
}

.mega-menu__group-title {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px 4px 0;
    color: #3c8f93;
    font-family: "Play", sans-serif;
    font-size: 1rem;
    font-weight: 700;
    text-decoration: none;
    line-height: 1.4;
    margin-top: 8px;
    transition: color 0.15s ease;
}

.mega-menu__group-title:first-child {
    margin-top: 0;
}

.mega-menu__group-title:hover,
.mega-menu__group-title--active {
    color: #e34444;
}

.mega-menu__link {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 12px 7px 0;
    color: #3c8f93;
    font-family: "Play", sans-serif;
    font-size: 0.9375rem;
    font-weight: 400;
    text-decoration: none;
    transition: color 0.15s ease;
    line-height: 1.4;
}

.mega-menu__link--child {
    padding-left: 8px;
    font-weight: 400;
}

.mega-menu__link:hover {
    color: #e34444;
}

.mega-menu__link--active {
    color: #e34444;
    font-weight: 600;
}

/* ========== COMPACT HEADER ========== */
.compact-logo {
    height: 48px;
}
@media (min-width: 1280px) {
    .compact-logo {
        height: 36px;
    }
    .compact-header {
        height: 48px;
    }
    .compact-search-bar {
        top: 48px;
    }
}

.compact-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1005;
    background: var(--m-surface);
    box-shadow: var(--m-shadow);
    height: 56px;
    display: flex;
    align-items: center;
    transform: translateY(-100%);
    opacity: 0;
    pointer-events: none;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
}
.compact-header--visible {
    transform: translateY(0);
    opacity: 1;
    pointer-events: auto;
}

.compact-nav {
    gap: 0;
    overflow-x: auto;
    flex-shrink: 1;
    min-width: 0;
}

.compact-nav__link {
    color: var(--m-primary);
    font-family: "Play", sans-serif;
    font-size: 13px;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.1px;
    padding: 4px 4px;
    text-decoration: none;
    cursor: pointer;
    transition: all var(--m-transition);
    display: inline-flex;
    align-items: center;
    gap: 2px;
    white-space: nowrap;
    border-radius: var(--m-radius);
}

.compact-nav__link:hover,
.compact-nav__link:focus-visible,
.compact-nav__link--active {
    color: var(--m-secondary);
}

.compact-nav__icon {
    transition: transform 0.2s ease;
    opacity: 0.6;
}

.compact-nav__icon--open {
    transform: rotate(180deg);
    opacity: 1;
}

.compact-nav__link--magazin {
    margin-left: auto;
    color: var(--m-secondary);
}
.compact-nav__link--magazin:hover {
    color: var(--m-secondary-dark, #c52f2f);
}

/* Compact search bar below sticky header */
.compact-search-bar {
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    z-index: 1004;
    background: #fff;
    border-bottom: 1px solid #eee;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

/* ========== MOBILE DRAWER ========== */
.mobile-drawer__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
}

.mobile-drawer__account {
    padding: 0 16px 14px;
    border-bottom: 1px solid #eee;
}

.mobile-drawer__user {
    display: flex;
    align-items: center;
    gap: 12px;
}

.mobile-drawer__user-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--m-on-surface);
}

.mobile-drawer__user-link {
    font-size: 0.75rem;
    color: var(--m-primary);
    text-decoration: none;
}
.mobile-drawer__user-link:hover {
    text-decoration: underline;
}

.mobile-drawer__auth-buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.mobile-drawer__nav {
    overflow-y: auto;
    flex: 1;
}

.mobile-drawer__back {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    cursor: pointer;
    background: #f5f5f5;
    color: #3c8f93;
    border-bottom: 1px solid #eee;
    transition: background 0.15s ease;
}

.mobile-drawer__back:hover {
    background: #eeeeee;
}

.mobile-drawer__all-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    font-weight: 600;
    color: #3c8f93;
    text-decoration: none;
    transition: background 0.15s ease;
}

.mobile-drawer__all-link:hover {
    background: rgba(60, 143, 147, 0.06);
}

.mobile-drawer__item {
    cursor: pointer;
}

.mobile-drawer__item--active {
    background: rgba(227, 68, 68, 0.06);
}

/* Slide transitions for mobile nav */
.slide-forward-enter-active,
.slide-forward-leave-active,
.slide-back-enter-active,
.slide-back-leave-active {
    transition: all 0.2s ease;
}

.slide-forward-enter-from {
    transform: translateX(30px);
    opacity: 0;
}

.slide-forward-leave-to {
    transform: translateX(-30px);
    opacity: 0;
}

.slide-back-enter-from {
    transform: translateX(-30px);
    opacity: 0;
}

.slide-back-leave-to {
    transform: translateX(30px);
    opacity: 0;
}

/* Expand transition */
.expand-enter-active,
.expand-leave-active {
    transition: all 0.3s ease;
    overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
    opacity: 0;
    max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
    opacity: 1;
    max-height: 100px;
}
</style>
